/**
 * Extension host — runtime loader + registry for vendor-signed UI extensions.
 *
 * Extensions are prebuilt ESM bundles served same-origin by the backend
 * (`/api/v1/ext-assets/...`), listed by the authenticated, entitlement-
 * filtered `/extensions/ui-manifest` endpoint. Each bundle calls
 * `window.TurboEA.register(key, plugin)` on import. React/MUI/the API
 * client are provided via `window.TurboEA.sdk` so bundles stay small and
 * share the host's single React instance (the template repo's Vite
 * config externalizes them onto these globals).
 *
 * Eight extension points exist: nav routes (full pages), card-detail tabs,
 * admin panels, custom field types (SDK 1.1 — a renderer/editor for an
 * `ext.{key}.*` field type used inside card attribute sections, plus — SDK 1.5
 * — an optional admin-side config editor for that type's config object), survey
 * templates (SDK 1.2), — SDK 1.3 — ADR panels (a component rendered on
 * the Architecture Decision Record editor/preview, e.g. a custom form
 * writing the ADR `ext.*` attributes bag) and ADR export sections (plain data
 * a plugin contributes into the core ADR DOCX export), and — SDK 1.4 — a
 * headless field-visibility provider (hides specific card fields at render
 * time from the extension's own, possibly async, logic; display-only, never
 * deletes stored values). Since SDK 1.6 extension reports can also participate
 * in Saved Reports: save with report_type "ext:{key}:{routeId}" via
 * `sdk.SaveReportDialog`, load via `sdk.useSavedReport`, and the gallery
 * resolves the namespaced type back to the registered route. Since SDK 1.7 the
 * sdk also carries the report-building kit core reports are made of —
 * `sdk.ReportShell` (frame + save/export/print), `sdk.FilterSelect`
 * (multi-select filter dropdown), and `sdk.CardDetailSidePanel` (card drawer)
 * — ReportShell/CardDetailSidePanel lazy-loaded with Suspense handled
 * internally. Since SDK 1.8 the sdk also carries `useCurrency` (workspace
 * currency formatting), `MetricCard` (KPI tile), `ReportLegend`,
 * `UserMultiSelect` (shared multi-user picker over GET /users), and
 * `loadRecharts` — an async loader returning the recharts module from core's
 * code-split chunk, so extensions never bundle a charting library. Since SDK
 * 1.9 `useChartTheme` returns the theme-aware Recharts chrome (grid stroke,
 * axis ticks, tooltip styling) core reports use, so extension charts match
 * core's look without hand-rolling it. Since SDK 1.11 `useThumbnailCapture`
 * captures a chart container as the PNG preview shown on saved-report cards
 * (html-to-image loads lazily on first capture). Since SDK 1.14
 * `CardScopeDialog` is the shared hierarchy-aware multi-select card picker
 * report scope controls are built on — pick cards and everything beneath them
 * comes with — plus its `dedupeScopeRoots` helper. Since SDK 1.15 the rest of
 * the kit ships too: `CardScopeFilter` (the toolbar chip that opens it),
 * `useCardScope` (scope state, closure, stale-id handling, type-change reset;
 * since SDK 1.31 `setScopeIds(ids, forType)` sets a scope together with the
 * card type it belongs to, so a restore that changes the type keeps it)
 * and `applyScope` (narrow a list to a closure). Since SDK 1.16
 * `integrationPanels` places an extension's integration configuration as a
 * sub-tab of Admin → Settings → Integrations, next to the built-in ServiceNow
 * integration (`adminPanels` is unchanged and still renders on Admin →
 * Extensions). Since SDK 1.21 the sdk carries `loadTimeline` (an async loader
 * resolving the time-travel `TimelineSlider` plus the `timelineRange` helpers
 * and `useTimeline`, so an extension timeline behaves exactly like the core
 * reports'), `loadDependencyView` (a lazy loader resolving the Layered
 * Dependency View renderer + its layout engine from core's code-split chunk —
 * extensions must never substitute another graph library), and the grid
 * filter-sidebar primitives `FilterSectionHeader` / `FilterCheckboxList` /
 * `ColumnFreezeToggle` (the §3.11 building blocks, so an extension's filter
 * sidebar cannot drift from the Inventory anatomy), and `loadReportExport`
 * (core's own XLSX/PPTX report exporters, so an extension never bundles a
 * presentation library and its decks match core's). Since SDK 1.24
 * `CardMultiPicker` is the full card browser — type rail with live counts,
 * hierarchy, subtree-root semantics via `roots`, and a selection that
 * survives re-faceting and re-searching — for anything bigger than the
 * single-type scope control `CardScopeDialog` covers. Since SDK 1.27
 * `CardPicker` is the single-select card picker every core dropdown that
 * selects ONE card is built on — browse on open, rank as you type, page in
 * more as the list scrolls — so an extension dialog that links one card
 * behaves exactly like core's instead of hand-rolling an Autocomplete.
 *
 * Since SDK 1.12 the preferred way to add a plug point is the GENERIC SLOT
 * registry, not a new named extension point. An extension declares
 * `slots: [{ slot, id, component | build, permission?, appliesTo?, order? }]`
 * on its plugin; core exposes a location by dropping `<ExtensionSlot
 * name="..." context={...} />` (component slots) or calling
 * `getExtensionSlots(name)` and rendering the returned `build(ctx)` data
 * itself (data slots — the adrGridColumns/adrExportSections philosophy). This
 * means a NEW plug location no longer needs an SDK type/hook/version change —
 * only a one-line `<ExtensionSlot>` in core with a fresh `slot` string. The
 * named points above are retained for compatibility; new locations should use
 * slots. Every extension-provided component must be rendered inside <ExtensionBoundary> —
 * a crashing extension shows a fallback chip, never a white screen. A field
 * type whose extension is missing, disabled, or unlicensed simply is not in
 * the registry, so core falls back to a read-only text rendering of the
 * stored value (the data is never lost).
 */

import * as mui from "@mui/material";
import { Alert } from "@mui/material";
import React, { useSyncExternalStore } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";

import { api } from "@/api/client";
import FilterSelect from "@/components/FilterSelect";
import CardMultiPicker from "@/components/CardMultiPicker";
import CardPicker from "@/components/CardPicker";
import LinkifiedText from "@/components/LinkifiedText";
import { sanitizeRichHtml } from "@/lib/richHtml";
import CardScopeDialog, { dedupeScopeRoots } from "@/components/CardScopeDialog";
import CardScopeFilter from "@/components/CardScopeFilter";
import { applyScope, useCardScope } from "@/hooks/useCardScope";
import MaterialSymbol from "@/components/MaterialSymbol";
import { hasPermission } from "@/components/RequirePermission";
import { useAuthContext } from "@/hooks/AuthContext";
import UserMultiSelect from "@/components/UserMultiSelect";
import MetricCard from "@/features/reports/MetricCard";
import ReportLegend from "@/features/reports/ReportLegend";
import SaveReportDialog from "@/features/reports/SaveReportDialog";
import type { ReportShellProps } from "@/features/reports/ReportShell";
import { useChartTheme } from "@/hooks/useChartTheme";
import { useThumbnailCapture } from "@/hooks/useThumbnailCapture";
import { useCurrency } from "@/hooks/useCurrency";
import { useSavedReport as useCoreSavedReport } from "@/hooks/useSavedReport";
import * as tokens from "@/theme/tokens";
// SDK 1.19 — choice-field pill rendering + metamodel label resolution, so an
// extension renders a select value exactly like core. All leaf/pure modules:
// no code-split graph, and no cycle back through cardDetailUtils.
import { OptionChip, SELECT_CHIP_BASE, chipWidthForField } from "@/components/OptionChip";
import { readableTextColor } from "@/lib/color";
import { fieldLabel, optionLabel, useFieldLabel, useOptionLabel } from "@/hooks/useResolveLabel";
// SDK 1.20 - the workspace date format, and in-app navigation. Both are things
// an extension can only otherwise approximate: a hand-rolled toLocaleDateString
// ignores Admin -> Settings -> Date format, and an <a href> to a core route
// reloads the whole SPA even though the extension page already renders inside
// core's router.
import { useDateFormat } from "@/hooks/useDateFormat";
import { useNavigate } from "react-router";
// SDK 1.23 — the shared commit-on-blur native date input (focus-protected
// draft), so an extension form gets the same date-entry behaviour as core
// and cannot reintroduce the mid-edit value-clobber bug (#865). MUI-only
// leaf module, so a static import is safe.
import DateField from "@/components/DateField";
// SDK 1.21 — the shared filter-sidebar building blocks (UI_GUIDELINES §3.11).
// MUI-only leaf modules like FilterSelect, so static imports are safe: no
// code-split graph is dragged into the eager bundle and no cycle back here.
import ColumnFreezeToggle from "@/components/grid/ColumnFreezeToggle";
import { FilterCheckboxList, FilterSectionHeader } from "@/components/FilterSidebarSection";
// SDK 1.25 — the card-detail lifecycle line, and gantt dependency-arrow
// routing. `LifecycleSection` is props-driven (it commits through an `onSave`
// callback and makes no API call of its own), so an extension can render a
// card's phases exactly as core does and wire the save to its own writer. It
// takes `PHASES`/`getPhaseLabels` from the leaf `@/lib/lifecyclePhases`
// rather than `cardDetailUtils`, which imports THIS module — a static import
// of the section is only safe because of that lift. `buildGanttArrowPath` is
// pure geometry, so a finish-to-start arrow drawn by an extension is the same
// shape as one drawn by the PPM gantt instead of a second convention.
import LifecycleSection from "@/features/cards/sections/LifecycleSection";
import { PHASES, getPhaseLabels } from "@/lib/lifecyclePhases";
import { buildGanttArrowPath } from "@/features/ppm/ganttArrowPath";
import type { ArchitectureDecision, Card } from "@/types";

export const UI_SDK_VERSION = "1.31";

/**
 * Core nav groups an extension route may request placement into (instead of the
 * default top-level nav entry). Whitelisted on purpose so an extension can only
 * land in sanctioned menus, never an arbitrary one; extend deliberately.
 *
 * `reports` and `grc` are top-bar dropdowns. `admin` (UI SDK 1.28) is the
 * Admin section of the user menu and the mobile drawer — the home for an
 * extension page that configures something rather than showing it, so it sits
 * beside Metamodel and Settings instead of claiming a top-bar icon every user
 * sees. It is gated by the route's own `permission` only: an extension decides
 * who may open its page, core decides where the entry lives.
 */
export const EXTENSION_NAV_GROUPS = ["reports", "grc", "admin"] as const;
export type ExtensionNavGroup = (typeof EXTENSION_NAV_GROUPS)[number];

export interface ExtensionRouteContribution {
  id: string;
  path: string; // mounted under /ext/{key}/... in the SPA router
  label: string;
  icon: string;
  permission?: string;
  component: React.ComponentType;
  // Optional placement hint: render this route's nav entry inside a core menu
  // group (e.g. "reports", or "admin" for the user menu's Admin section) rather
  // than as a top-level item. The route path and rendering are unchanged — only
  // where the menu entry appears. Omit for the current top-level behaviour. An
  // unrecognised value shows nowhere in the nav.
  navGroup?: ExtensionNavGroup;
  // Optional placement hint for a TOP-LEVEL entry: where in the bar it sits.
  // `start` / `end` / `before:<anchor>` / `after:<anchor>`, anchors being the
  // core nav keys in `NAV_ANCHORS` — the same grammar a manifest field section
  // uses to place itself on a card type. Defaults to `end`, which is the
  // behaviour every extension had before this existed, and an anchor not
  // currently in the bar (a module switched off) degrades to that default
  // rather than dropping the entry.
  //
  // Ignored when `navGroup` is set: the entry is then a child of that group,
  // and where the group itself sits is core's business.
  navPlacement?: string;
}

export interface ExtensionCardTabContribution {
  id: string;
  label: string;
  permission?: string;
  appliesTo?: string[]; // card-type keys; omit = all types
  component: React.ComponentType<{ cardId: string; cardType: string }>;
}

export interface ExtensionAdminPanelContribution {
  id: string;
  label: string;
  component: React.ComponentType;
}

/**
 * A panel rendered as a sub-tab of Admin → Settings → Integrations (SDK 1.16),
 * alongside the built-in ServiceNow integration and with the same chrome — the
 * sanctioned home for an extension that connects Turbo EA to an external
 * system (its connection settings, sync configuration, logs). A typed named
 * point rather than a generic slot because the hub needs display metadata
 * (label + icon) to build the sub-tab list before mounting anything.
 * `permission` hides the entry silently from users who lack it. `adminPanels`
 * is unchanged and still renders on Admin → Extensions; an integration opts
 * into the Integrations tab explicitly with this point.
 */
export interface ExtensionIntegrationPanelContribution {
  id: string;
  label: string; // sub-tab text — the extension localizes it at registration time
  icon?: string; // Material Symbols name shown next to the label
  permission?: string;
  component: React.ComponentType;
}

/** Props handed to an extension field type's read-only display component. */
export interface ExtensionFieldDisplayProps {
  field: { key: string; label: string; type: string; config?: Record<string, unknown> };
  value: unknown;
  config?: Record<string, unknown>;
}

/** Props handed to an extension field type's inline editor component. */
export interface ExtensionFieldEditorProps extends ExtensionFieldDisplayProps {
  onChange: (value: unknown) => void;
  error?: string;
}

/**
 * A custom field type an extension contributes to card attribute editing.
 * `type` MUST be namespaced `ext.{extensionKey}.*` (enforced at aggregation) so
 * two extensions can never collide and core can tell a custom type from an
 * unknown one. `config` is the per-field settings object (e.g. `{min,max}` for a
 * rating), sourced from the field's `config` in the metamodel.
 */
export interface ExtensionFieldTypeContribution {
  type: string;
  label: string;
  display?: React.ComponentType<ExtensionFieldDisplayProps>;
  editor?: React.ComponentType<ExtensionFieldEditorProps>;
  defaultConfig?: Record<string, unknown>;
  // Optional admin-side editor for this type's `config` object (SDK 1.5). When
  // present, the metamodel field editor mounts it (inside ExtensionBoundary)
  // instead of the generic type-aware config editor — so an extension can offer
  // a proper per-locale rubric/question editor. Falls back to the generic editor
  // when absent.
  configEditor?: React.ComponentType<{
    config: Record<string, unknown>;
    onChange: (config: Record<string, unknown>) => void;
  }>;
}

/** The payload an extension survey template posts to `POST /surveys`. */
export interface ExtensionSurveyTemplatePayload {
  name: string;
  description?: string;
  message?: string;
  target_type_key: string;
  target_filters?: Record<string, unknown>;
  target_roles?: string[];
  fields?: Array<Record<string, unknown>>;
}

/**
 * A "New from template" shortcut on the Surveys admin page. Clicking it mints a
 * fresh **draft** survey from `build()`'s payload (POST /surveys always creates
 * a draft) and opens it in the builder for the admin to review and send —
 * `build()` runs at click time so it may compute dynamic values (dates, etc.).
 */
export interface ExtensionSurveyTemplateContribution {
  id: string;
  label: string;
  icon?: string;
  build: () => ExtensionSurveyTemplatePayload;
}

/** Props every ADR panel component receives (SDK 1.3). */
export interface ExtensionAdrPanelProps {
  adrId: string;
  status: string; // draft | in_review | signed
  signed: boolean; // convenience: status === "signed" (attributes are frozen)
  // The panel must not allow edits when true: it is either a signed (frozen)
  // ADR or the read-only preview page. Editing is only offered on the editor
  // page for an unsigned ADR.
  readOnly: boolean;
}

/**
 * A panel rendered on the ADR editor and preview pages (SDK 1.3). ADRs are not
 * cards, so this is the sanctioned place to attach ADR-scoped UI — e.g. a
 * custom form that reads/writes the ADR `ext.*` attributes bag via
 * `PATCH /adr/{id}`. `appliesTo` has no analogue (there is only one ADR
 * "type"); gate with `permission` if needed. Rendered read-only-friendly: the
 * component should respect `signed` to disable editing once frozen.
 */
export interface ExtensionAdrPanelContribution {
  id: string;
  permission?: string;
  component: React.ComponentType<ExtensionAdrPanelProps>;
}

/** A section a plugin contributes into the core ADR DOCX export (SDK 1.3). */
export interface AdrExportSection {
  heading: string;
  paragraphs?: string[];
  table?: { headers: string[]; rows: string[][] };
}

/**
 * Contributes extra sections to the core ADR DOCX export (SDK 1.3). `build()`
 * receives the full ADR (including its `attributes` bag) and returns plain data
 * — core renders it with the document's own styles, so the plugin never touches
 * the `docx` library. Runs at export time inside a guard; a throw is skipped.
 */
export interface ExtensionAdrExportContribution {
  id: string;
  build: (adr: Record<string, unknown>) => AdrExportSection[];
}

/**
 * A plain-data column appended to the shared ADR grid (SDK 1.10) — the list
 * used by EA Delivery → Decisions and GRC → Governance → Decisions. Core
 * builds a native AG Grid ColDef from it, so the column is indistinguishable
 * from the built-in ones (theme, sorting, quick filter, resize, dark mode) and
 * a broken extension can never take the grid down: `value()`/`sortValue()`
 * run inside a guard — a throw yields an empty cell. `value()` returns the
 * display text (the ADR list summaries already carry the `attributes` bag, so
 * a column over `ext.*` data costs zero extra requests); `sortValue()`
 * optionally supplies the raw sortable value (e.g. a number behind a
 * currency-formatted string). Deliberately NOT a component — extensions
 * contribute data, core owns the rendering, same philosophy as
 * `adrExportSections`.
 */
export interface ExtensionAdrGridColumnContribution {
  id: string;
  label: string; // header text — the extension localizes it at registration time
  align?: "left" | "right";
  // Optional sizing hints for the AG Grid column (px). Default 150 / 120 when
  // omitted. The user can still resize; these set the initial width.
  width?: number;
  minWidth?: number;
  value: (adr: ArchitectureDecision) => string | null;
  sortValue?: (adr: ArchitectureDecision) => number | string | null;
}

/** Props a headless field-visibility provider receives (SDK 1.4). */
export interface ExtensionFieldVisibilityProps {
  card: Card;
  // Report which of the extension's own card fields to hide right now. Call on
  // mount and whenever the provider's (possibly async) logic resolves or
  // changes; reporting `[]` shows everything. `extKey` keys the report so
  // multiple providers' hidden sets union without clobbering each other — pass
  // the extension's own key (the same one it registered with).
  report: (extKey: string, hiddenKeys: string[]) => void;
}

/**
 * A generic slot contribution (SDK 1.12) — the forward path for adding plug
 * points without an SDK edit per location. `slot` is the location id an
 * extension targets (e.g. "card.detail.header", "risk.detail.panel"); core
 * exposes each location once via `<ExtensionSlot name="..." />` (component
 * slots) or `getExtensionSlots(name)` (data slots). A contribution supplies
 * exactly ONE of `component` (rendered by core inside an ExtensionBoundary,
 * receiving the slot's `context` as props) or `build` (returns plain data core
 * renders itself). `permission` hides the slot from users who lack it (silently
 * — never an access-denied page, unlike a full route); `appliesTo` optionally
 * filters on the context's `cardType`/`type`; `order` sorts within a slot
 * (default 0, ties keep registration order).
 *
 * Slot locations core exposes today:
 *   - `card.detail.header`  (component) — CardDetailContent. Core renders the
 *     contributions as ONE wrapping flex row (8px gap, hidden when empty), so
 *     contribute an inline item (a Chip) with no outer margins of its own; a
 *     contribution that needs its own line sets `flexBasis: "100%"`, and
 *     `order` decides where it sits in the row. The row ZEROES a contribution's
 *     own margins rather than trusting it not to set any: `align-items: center`
 *     centres a flex item's margin box, so a bundle built before this row —
 *     which spaces its own band — would otherwise render off the row's centre
 *     line.
 *   - `risk.detail.panel`   (component) — RiskDetailPage
 *   - `adr.header`, `adr.signature.footer` (component) — ADREditor/ADRPreview
 *   - `notification.preferences.channels` (data) — one column per
 *     extension-delivered notification channel. `build({channelKey})` returns
 *     `{label, order?}`; the column only renders when the BACKEND also reports
 *     the channel as live, so a UI-only bundle cannot conjure a column whose
 *     PATCH the backend would ignore.
 *   - `notification.preferences.footer` (component) — below the preferences
 *     table, context `{userId}`; where a channel shows its per-user link state.
 *   - `notification.detail` (component) — inside the bell's notification
 *     details dialog (opened for a notification sent with `detail=True` on the
 *     backend notify bridge), context `{id, type, title, message, link, data,
 *     cardId, createdAt}`. Rendered with `ownerExtKey = data.ext`, so only the
 *     extension that SENT the notification contributes; target your own
 *     declared type with `appliesTo` and never gate it with `permission` — the
 *     recipients are ordinary users.
 */
export interface ExtensionSlotContribution {
  slot: string;
  id: string;
  permission?: string;
  appliesTo?: string[];
  component?: React.ComponentType<Record<string, unknown>>;
  build?: (context: Record<string, unknown>) => unknown;
  order?: number;
}

export interface TurboExtensionUI {
  key: string;
  sdkVersion: string;
  routes?: ExtensionRouteContribution[];
  cardTabs?: ExtensionCardTabContribution[];
  adminPanels?: ExtensionAdminPanelContribution[];
  integrationPanels?: ExtensionIntegrationPanelContribution[];
  fieldTypes?: ExtensionFieldTypeContribution[];
  surveyTemplates?: ExtensionSurveyTemplateContribution[];
  adrPanels?: ExtensionAdrPanelContribution[];
  adrExportSections?: ExtensionAdrExportContribution[];
  adrGridColumns?: ExtensionAdrGridColumnContribution[];
  // Generic slot contributions (SDK 1.12) — the preferred way to attach to any
  // core-exposed location without a per-feature SDK extension point.
  slots?: ExtensionSlotContribution[];
  // Headless provider (renders null) that hides specific card fields at render
  // time — display-only, ungated, never deletes stored values. Degrades to
  // "show everything" when absent.
  fieldVisibility?: React.ComponentType<ExtensionFieldVisibilityProps>;
}

export interface RegisteredFieldType {
  extKey: string;
  contribution: ExtensionFieldTypeContribution;
}

export interface RegisteredSlot {
  extKey: string;
  contribution: ExtensionSlotContribution;
}

export interface RegisteredIntegrationPanel {
  extKey: string;
  contribution: ExtensionIntegrationPanelContribution;
}

export interface RegisteredSurveyTemplate {
  extKey: string;
  contribution: ExtensionSurveyTemplateContribution;
}

export interface RegisteredAdrPanel {
  extKey: string;
  contribution: ExtensionAdrPanelContribution;
}

export interface RegisteredAdrExport {
  extKey: string;
  contribution: ExtensionAdrExportContribution;
}

export interface RegisteredAdrGridColumn {
  extKey: string;
  contribution: ExtensionAdrGridColumnContribution;
}

export interface RegisteredFieldVisibility {
  extKey: string;
  provider: React.ComponentType<ExtensionFieldVisibilityProps>;
}

export interface RegisteredExtension {
  key: string;
  plugin: TurboExtensionUI;
}

interface UiManifestEntry {
  key: string;
  name: string;
  version: string;
  entry: string;
  entitlement_state: string;
}

// ---------------------------------------------------------------------------
// Registry store (useSyncExternalStore-backed so mount points re-render)
// ---------------------------------------------------------------------------

let _registered: RegisteredExtension[] = [];
let _loadErrors: Record<string, string> = {};
// Display names from the UI manifest, keyed by extension key — so the bell
// can say who sent a notification without a second request.
let _displayNames: Record<string, string> = {};
const _listeners = new Set<() => void>();
let _loadStarted = false;
// Cached, stable snapshots — recomputed lazily and invalidated on every
// notify() so useSyncExternalStore never loops.
let _fieldTypesCache: Record<string, RegisteredFieldType> | null = null;
let _surveyTemplatesCache: RegisteredSurveyTemplate[] | null = null;
let _adrExportCache: RegisteredAdrExport[] | null = null;
let _adrGridColumnsCache: RegisteredAdrGridColumn[] | null = null;
let _fieldVisibilityCache: RegisteredFieldVisibility[] | null = null;
// Generic slots are cached per slot name — one stable snapshot array per name so
// each `getExtensionSlots(name)` is safe as a useSyncExternalStore snapshot.
let _slotsCache: Map<string, RegisteredSlot[]> | null = null;
let _integrationPanelsCache: RegisteredIntegrationPanel[] | null = null;

function notify() {
  _fieldTypesCache = null;
  _surveyTemplatesCache = null;
  _adrExportCache = null;
  _adrGridColumnsCache = null;
  _fieldVisibilityCache = null;
  _slotsCache = null;
  _integrationPanelsCache = null;
  _listeners.forEach((fn) => fn());
}

function subscribe(fn: () => void): () => void {
  _listeners.add(fn);
  return () => _listeners.delete(fn);
}

function sdkMajor(v: string): number {
  return Number.parseInt(String(v).split(".", 1)[0] ?? "", 10);
}

export function registerExtension(key: string, plugin: TurboExtensionUI): void {
  if (!plugin || plugin.key !== key) {
    _loadErrors = { ..._loadErrors, [key]: `register() key mismatch (${plugin?.key})` };
    notify();
    return;
  }
  if (sdkMajor(plugin.sdkVersion) !== sdkMajor(UI_SDK_VERSION)) {
    _loadErrors = {
      ..._loadErrors,
      [key]: `built for UI SDK ${plugin.sdkVersion}, host provides ${UI_SDK_VERSION}`,
    };
    notify();
    return;
  }
  _registered = [..._registered.filter((r) => r.key !== key), { key, plugin }];
  notify();
}

export function getRegisteredExtensions(): RegisteredExtension[] {
  return _registered;
}

export function getExtensionLoadErrors(): Record<string, string> {
  return _loadErrors;
}

export function useExtensionUI(): RegisteredExtension[] {
  return useSyncExternalStore(subscribe, getRegisteredExtensions, getRegisteredExtensions);
}

/**
 * Map of every registered custom field type (`ext.{key}.*` → contribution).
 * A contribution whose `type` is not namespaced under its own extension key is
 * dropped (defence against collisions). The result is cached and only changes
 * when the registry does, so it is safe as a useSyncExternalStore snapshot.
 */
export function getExtensionFieldTypes(): Record<string, RegisteredFieldType> {
  if (_fieldTypesCache) return _fieldTypesCache;
  const out: Record<string, RegisteredFieldType> = {};
  for (const { key, plugin } of _registered) {
    for (const ft of plugin.fieldTypes ?? []) {
      if (!ft?.type || !ft.type.startsWith(`ext.${key}.`)) {
        console.warn(
          `[extension:${key}] field type "${ft?.type}" must be namespaced ext.${key}.* — ignored`,
        );
        continue;
      }
      out[ft.type] = { extKey: key, contribution: ft };
    }
  }
  _fieldTypesCache = out;
  return out;
}

export function useExtensionFieldTypes(): Record<string, RegisteredFieldType> {
  return useSyncExternalStore(subscribe, getExtensionFieldTypes, getExtensionFieldTypes);
}

/**
 * Survey templates contributed by registered extensions, in registration order.
 * A contribution missing `id`/`label`/`build` is dropped. Cached (stable
 * reference) so it is safe as a useSyncExternalStore snapshot.
 */
export function getExtensionSurveyTemplates(): RegisteredSurveyTemplate[] {
  if (_surveyTemplatesCache) return _surveyTemplatesCache;
  const out: RegisteredSurveyTemplate[] = [];
  for (const { key, plugin } of _registered) {
    for (const tpl of plugin.surveyTemplates ?? []) {
      if (!tpl?.id || !tpl.label || typeof tpl.build !== "function") {
        console.warn(`[extension:${key}] invalid survey template — ignored`, tpl);
        continue;
      }
      out.push({ extKey: key, contribution: tpl });
    }
  }
  _surveyTemplatesCache = out;
  return out;
}

export function useExtensionSurveyTemplates(): RegisteredSurveyTemplate[] {
  return useSyncExternalStore(
    subscribe,
    getExtensionSurveyTemplates,
    getExtensionSurveyTemplates,
  );
}

/**
 * Headless field-visibility providers contributed by registered extensions, in
 * registration order (one per extension at most). A non-function `fieldVisibility`
 * is dropped. Cached (stable reference) so the mount point can render a fixed
 * list of provider slots — each provider keeps its own hook order across
 * re-renders — and so it is safe as a useSyncExternalStore snapshot.
 */
export function getExtensionFieldVisibilityProviders(): RegisteredFieldVisibility[] {
  if (_fieldVisibilityCache) return _fieldVisibilityCache;
  const out: RegisteredFieldVisibility[] = [];
  for (const { key, plugin } of _registered) {
    const provider = plugin.fieldVisibility;
    if (provider === undefined) continue;
    if (typeof provider !== "function") {
      console.warn(`[extension:${key}] invalid fieldVisibility provider — ignored`, provider);
      continue;
    }
    out.push({ extKey: key, provider });
  }
  _fieldVisibilityCache = out;
  return out;
}

export function useExtensionFieldVisibilityProviders(): RegisteredFieldVisibility[] {
  return useSyncExternalStore(
    subscribe,
    getExtensionFieldVisibilityProviders,
    getExtensionFieldVisibilityProviders,
  );
}

/**
 * SDK 1.7 — the report-building kit core reports are made of, so an extension
 * report gets the identical frame/UX with zero reimplementation. All ungated —
 * RBAC stays server-side on whatever the pieces fetch. Exposed on
 * `window.TurboEA.sdk` as `ReportShell`, `FilterSelect`, `CardDetailSidePanel`.
 * All three work on /ext/* routes (they only need the SPA router + the
 * `reports`/`common` i18n namespaces, both always loaded).
 *
 * - ReportShell — the report frame: title, saved-report banner, save button,
 *   export/print, filters toolbar slot, actions slot, thumbnail chart ref.
 * - FilterSelect — the shared multi-select filter dropdown.
 * - CardDetailSidePanel — the self-contained card-detail drawer reports open
 *   on node/row click ({cardId, open, onClose}).
 *
 * ReportShell and CardDetailSidePanel are deliberately LAZY: ReportShell pulls
 * the export engine (xlsx / pptxgenjs / html-to-image) and the panel pulls the
 * whole card-detail graph (CardDetailContent + tabs) — both are code-split
 * behind lazy routes today, and a static import here would drag them into the
 * eager main bundle (the panel would also create a module cycle:
 * CardDetailContent imports this module). Suspense is handled internally so
 * extensions just render the components. FilterSelect is MUI-only and is
 * imported statically.
 */
const LazyCardDetailSidePanel = React.lazy(() => import("@/components/CardDetailSidePanel"));
const LazyReportShell = React.lazy(() => import("@/features/reports/ReportShell"));
const LazyCreateCardDialog = React.lazy(() => import("@/components/CreateCardDialog"));

export function ExtensionCardDetailSidePanel(props: {
  cardId: string | null;
  open: boolean;
  onClose: () => void;
}) {
  return (
    <React.Suspense fallback={null}>
      <LazyCardDetailSidePanel {...props} />
    </React.Suspense>
  );
}

export function ExtensionReportShell(props: ReportShellProps) {
  return (
    <React.Suspense fallback={null}>
      <LazyReportShell {...props} />
    </React.Suspense>
  );
}

/**
 * SDK 1.17 — core's create-card dialog for extension pages. Lazy for the
 * same reason as the side panel (AiSuggestPanel/CardPicker graph). The
 * caller supplies `onCreate` (typically `api.post("/cards", data)` resolving
 * to the new card id); `initialType`/`initialSubtype`/`initialAttributes`
 * open it pre-configured. On success the dialog navigates to the new card.
 */
export function ExtensionCreateCardDialog(props: {
  open: boolean;
  onClose: () => void;
  onCreate: (data: {
    type: string;
    subtype?: string;
    name: string;
    description?: string;
    parent_id?: string;
    attributes?: Record<string, unknown>;
    lifecycle?: Record<string, string>;
  }) => Promise<string>;
  initialType?: string;
  initialSubtype?: string;
  initialAttributes?: Record<string, unknown>;
}) {
  return (
    <React.Suspense fallback={null}>
      <LazyCreateCardDialog {...props} />
    </React.Suspense>
  );
}

/**
 * SDK 1.6 — saved-report participation for extension reports.
 *
 * An extension report saves with `report_type: "ext:{key}:{routeId}"` (the
 * gallery resolves it back to the registered route) and uses this hook to load
 * a saved configuration when opened via `?saved_report_id=`. Returns nulls
 * when the page was opened without a saved report. Exposed on
 * `window.TurboEA.sdk.useSavedReport`; pairs with `sdk.SaveReportDialog` (the
 * core save/share dialog re-exported verbatim: name/description/visibility/
 * share-with-users → POST /saved-reports). Ungated — permissions are the same
 * `saved_reports.*` keys every core report uses, enforced server-side.
 *
 * SDK 1.13 additionally re-exports the core hook's **localStorage
 * auto-persist** layer — `consumeConfig` / `persistConfig` / `resetAll` — so an
 * extension report keeps its filters + selection across a page refresh exactly
 * like a core report (same three-tier priority: URL saved report > localStorage
 * > component defaults). `consumeConfig()` is called once on mount to restore,
 * `persistConfig(config)` on every change to auto-save, and `resetAll()` clears
 * both localStorage and the URL saved report. See any core report
 * (`CostReport.tsx`) for the canonical usage.
 */
export function useExtensionSavedReport(reportType: string): {
  config: Record<string, unknown> | null;
  savedReportId: string | null;
  name: string | null;
  consumeConfig: () => Record<string, unknown> | null;
  persistConfig: (config: Record<string, unknown>) => void;
  resetAll: () => void;
} {
  const saved = useCoreSavedReport(reportType);
  return {
    config: (saved.savedReport?.config as Record<string, unknown> | undefined) ?? null,
    savedReportId: saved.savedReport?.id ?? null,
    name: saved.savedReportName,
    consumeConfig: saved.consumeConfig,
    persistConfig: saved.persistConfig,
    resetAll: saved.resetAll,
  };
}

/**
 * ADR panels contributed by registered extensions, in registration order.
 * Consumed via `useExtensionUI()` at the mount points (ADR editor/preview); a
 * contribution missing `id`/`component` is dropped. Not cached — the caller
 * already re-renders off `useExtensionUI`.
 */
export function getExtensionAdrPanels(): RegisteredAdrPanel[] {
  const out: RegisteredAdrPanel[] = [];
  for (const { key, plugin } of _registered) {
    for (const panel of plugin.adrPanels ?? []) {
      if (!panel?.id || typeof panel.component !== "function") {
        console.warn(`[extension:${key}] invalid ADR panel — ignored`, panel);
        continue;
      }
      out.push({ extKey: key, contribution: panel });
    }
  }
  return out;
}

export function useExtensionAdrPanels(): RegisteredAdrPanel[] {
  // Depend on the registry snapshot so mounts re-render on (un)register.
  useSyncExternalStore(subscribe, getRegisteredExtensions, getRegisteredExtensions);
  return getExtensionAdrPanels();
}

/**
 * ADR DOCX export contributions, in registration order. A contribution missing
 * `id`/`build` is dropped. Cached (stable reference) so the non-React exporter
 * (`adrExport.ts`) can call it directly.
 */
export function getExtensionAdrExportSections(): RegisteredAdrExport[] {
  if (_adrExportCache) return _adrExportCache;
  const out: RegisteredAdrExport[] = [];
  for (const { key, plugin } of _registered) {
    for (const contribution of plugin.adrExportSections ?? []) {
      if (!contribution?.id || typeof contribution.build !== "function") {
        console.warn(`[extension:${key}] invalid ADR export contribution — ignored`, contribution);
        continue;
      }
      out.push({ extKey: key, contribution });
    }
  }
  _adrExportCache = out;
  return out;
}

/**
 * ADR grid column contributions (SDK 1.10), in registration order. A
 * contribution missing `id`/`label`/`value` is dropped. Cached (stable
 * reference) so it is safe as a useSyncExternalStore snapshot — the grid's
 * memoized columnDefs only rebuild when the registry actually changes.
 */
export function getExtensionAdrGridColumns(): RegisteredAdrGridColumn[] {
  if (_adrGridColumnsCache) return _adrGridColumnsCache;
  const out: RegisteredAdrGridColumn[] = [];
  for (const { key, plugin } of _registered) {
    for (const contribution of plugin.adrGridColumns ?? []) {
      if (!contribution?.id || !contribution.label || typeof contribution.value !== "function") {
        console.warn(`[extension:${key}] invalid ADR grid column — ignored`, contribution);
        continue;
      }
      out.push({ extKey: key, contribution });
    }
  }
  _adrGridColumnsCache = out;
  return out;
}

export function useExtensionAdrGridColumns(): RegisteredAdrGridColumn[] {
  return useSyncExternalStore(subscribe, getExtensionAdrGridColumns, getExtensionAdrGridColumns);
}

/**
 * Integration panels contributed by registered extensions (SDK 1.16), in
 * registration order. Rendered as sub-tabs of Admin → Settings → Integrations
 * next to the built-in ServiceNow integration; the hub filters `permission`
 * itself so a hidden entry never even shows a tab. A contribution missing
 * `id`/`label`/`component` is dropped. Cached (stable reference) so it is safe
 * as a useSyncExternalStore snapshot.
 */
export function getExtensionIntegrationPanels(): RegisteredIntegrationPanel[] {
  if (_integrationPanelsCache) return _integrationPanelsCache;
  const out: RegisteredIntegrationPanel[] = [];
  for (const { key, plugin } of _registered) {
    for (const panel of plugin.integrationPanels ?? []) {
      if (!panel?.id || !panel.label || typeof panel.component !== "function") {
        console.warn(`[extension:${key}] invalid integration panel — ignored`, panel);
        continue;
      }
      out.push({ extKey: key, contribution: panel });
    }
  }
  _integrationPanelsCache = out;
  return out;
}

export function useExtensionIntegrationPanels(): RegisteredIntegrationPanel[] {
  return useSyncExternalStore(
    subscribe,
    getExtensionIntegrationPanels,
    getExtensionIntegrationPanels,
  );
}

/**
 * Generic slot contributions for a given `slot` name (SDK 1.12), in `order`
 * then registration order. A contribution without an `id`, or that does not
 * supply exactly one of `component`/`build`, is dropped with a warning. Cached
 * per slot name (stable array reference until the registry changes) so it is
 * safe both as a useSyncExternalStore snapshot and for non-React data-slot
 * consumers to call directly.
 */
export function getExtensionSlots(slot: string): RegisteredSlot[] {
  if (!_slotsCache) _slotsCache = new Map();
  const cached = _slotsCache.get(slot);
  if (cached) return cached;
  const out: RegisteredSlot[] = [];
  for (const { key, plugin } of _registered) {
    for (const contribution of plugin.slots ?? []) {
      if (contribution?.slot !== slot) continue;
      const hasComponent = typeof contribution.component === "function";
      const hasBuild = typeof contribution.build === "function";
      // Require an id and EXACTLY one of component/build (XOR).
      if (!contribution.id || hasComponent === hasBuild) {
        console.warn(
          `[extension:${key}] invalid slot contribution for "${slot}" ` +
            `(needs id + exactly one of component/build) — ignored`,
          contribution,
        );
        continue;
      }
      out.push({ extKey: key, contribution });
    }
  }
  out.sort((a, b) => (a.contribution.order ?? 0) - (b.contribution.order ?? 0));
  _slotsCache.set(slot, out);
  return out;
}

export function useExtensionSlots(slot: string): RegisteredSlot[] {
  const getSnapshot = React.useCallback(() => getExtensionSlots(slot), [slot]);
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/**
 * Renders every component slot registered for `name`, each inside an
 * ExtensionBoundary, passing `context` as props. Core exposes a new pluggable
 * location by dropping this once — no SDK type/hook/version change. A slot with
 * a `permission` the viewer lacks is hidden silently (not an access-denied
 * page); `appliesTo` filters on `context.cardType`/`context.type`. Data slots
 * (a `build` instead of a `component`) are not rendered here — their core
 * consumer calls `getExtensionSlots(name)` and renders the data itself.
 */
export function ExtensionSlot({
  name,
  context,
  ownerExtKey,
}: {
  name: string;
  context?: Record<string, unknown>;
  /** When set, only this extension's contributions render — for a location
   *  that belongs to one extension's own data (its notification's details),
   *  where a second extension must not inject into the first's content. */
  ownerExtKey?: string;
}) {
  const slots = useExtensionSlots(name);
  const { user } = useAuthContext();
  const typeKey = context?.cardType ?? context?.type;
  return (
    <>
      {slots.map(({ extKey, contribution }) => {
        const Component = contribution.component;
        if (!Component) return null; // data slot — rendered by its own core consumer
        if (ownerExtKey !== undefined && extKey !== ownerExtKey) return null;
        if (
          contribution.permission &&
          !hasPermission(user?.permissions, contribution.permission)
        ) {
          return null;
        }
        if (
          contribution.appliesTo &&
          !(typeof typeKey === "string" && contribution.appliesTo.includes(typeKey))
        ) {
          return null;
        }
        return (
          <ExtensionBoundary key={`${extKey}:${contribution.id}`} extensionKey={extKey}>
            <Component {...(context ?? {})} />
          </ExtensionBoundary>
        );
      })}
    </>
  );
}

/** Test helper — wipe the registry between tests. */
export function resetExtensionHost(): void {
  _registered = [];
  _loadErrors = {};
  _displayNames = {};
  _loadStarted = false;
  _fieldTypesCache = null;
  _slotsCache = null;
  _integrationPanelsCache = null;
  notify();
}

// ---------------------------------------------------------------------------
// window.TurboEA — the global SDK surface extension bundles compile against
// ---------------------------------------------------------------------------

declare global {
  interface Window {
    TurboEA?: {
      sdk: Record<string, unknown>;
      register: (key: string, plugin: TurboExtensionUI) => void;
    };
  }
}

export function initExtensionHost(): void {
  if (typeof window === "undefined" || window.TurboEA) return;
  window.TurboEA = {
    sdk: {
      React,
      ReactDOM,
      mui,
      api,
      MaterialSymbol,
      useTranslation,
      tokens,
      uiSdkVersion: UI_SDK_VERSION,
      // SDK 1.19 — render a select option the way core does.
      OptionChip,
      SELECT_CHIP_BASE,
      chipWidthForField,
      readableTextColor,
      useOptionLabel,
      useFieldLabel,
      optionLabel,
      fieldLabel,
      // SDK 1.6 — saved-report participation (see useExtensionSavedReport).
      useSavedReport: useExtensionSavedReport,
      SaveReportDialog,
      // SDK 1.7 — report-building kit (ReportShell/CardDetailSidePanel are
      // lazy wrappers; see the doc block above ExtensionCardDetailSidePanel).
      ReportShell: ExtensionReportShell,
      FilterSelect,
      CardDetailSidePanel: ExtensionCardDetailSidePanel,
      // SDK 1.8 — dashboard-building additions: currency formatting, KPI tile,
      // legend, the shared multi-user picker, and a lazy Recharts loader so
      // extension charts reuse core's code-split chunk instead of bundling
      // their own copy (a static import here would drag Recharts into the
      // eager main bundle — same caveat as ReportShell above).
      useCurrency,
      MetricCard,
      ReportLegend,
      UserMultiSelect,
      // SDK 1.20 - `useDateFormat` returns the same bound `formatDate` /
      // `formatDateTime` core renders every date through, so an extension
      // follows the workspace setting instead of inventing a format.
      // `useNavigate` is react-router's, re-exported: an extension page is
      // mounted inside core's router, so it can route without a document
      // reload - but it must not bundle react-router itself.
      useDateFormat,
      useNavigate,
      // SDK 1.23 — the shared native date input (see the import note above).
      DateField,
      LifecycleSection,
      PHASES,
      getPhaseLabels,
      buildGanttArrowPath,
      loadRecharts: () => import("recharts"),
      // SDK 1.9 — theme-aware Recharts chrome (grid/axis/tooltip), the same
      // conventions core reports use, so extension charts cannot drift from
      // core's look in either light or dark mode.
      useChartTheme,
      useThumbnailCapture,
      // SDK 1.14 — the shared card scope picker. An extension report that
      // wants "narrow this to a few cards (and everything under them)" gets
      // core's behaviour — browse-on-open, tree, ancestor-wins dedupe — rather
      // than re-rolling a weaker one. MUI-only, so a static import is fine.
      CardScopeDialog,
      dedupeScopeRoots,
      // SDK 1.24 — the full card browser. `CardScopeDialog` stays the compact
      // control a report toolbar opens over ONE type; this is the one to reach
      // for when the user picks across types, or when the caller doesn't know
      // in advance which type they want. MUI-only leaf, so a static import.
      CardMultiPicker,
      // SDK 1.27 — the single-select card picker (CLAUDE.md: *always use
      // CardPicker*). Same engine as the multi picker, for the dialog that
      // links ONE card. MUI-only leaf, so a static import.
      CardPicker,
      // SDK 1.29 — free text and stored HTML. `LinkifiedText` renders a
      // user-typed string with its http(s) addresses as new-tab links (a
      // fragment, so it sits inside the caller's own Typography);
      // `sanitizeRichHtml` is the one sanitiser for stored rich text — it
      // autolinks bare URLs and stamps every anchor target/rel. An extension
      // that renders a description or a rich-text field must not rebuild
      // either, or its links will behave differently from core's.
      LinkifiedText,
      sanitizeRichHtml,
      // SDK 1.15 — the whole report-scoping kit, so an extension report gets
      // "narrow this to a few cards and everything under them" with the same
      // saved-report round-trip and stale-id handling core reports have.
      CardScopeFilter,
      useCardScope,
      applyScope,
      // SDK 1.17 — data-grid + card-creation reuse. `loadAgGrid` resolves
      // core's code-split AG Grid chunk (module registration + the Theming
      // API themes ride along via agGridSetup) so extension grids share
      // core's grid look without bundling AG Grid; `CreateCardDialog` is
      // core's create-card modal (lazy wrapper), openable pre-configured
      // via initialType/initialSubtype/initialAttributes.
      // Since SDK 1.18 the resolved module also carries the documented grid
      // template hooks (UI_GUIDELINES §3.6) — `useColumnFreeze` and
      // `useColumnOrder` — lazily, so they stay in the code-split grid chunk.
      // Extension components call them off the loaded module; that is safe
      // because the grid component only mounts once the module resolved, so
      // hook call order stays stable.
      // Since SDK 1.22 it also resolves `useCellContextMenu`, so an
      // extension grid offers the same right-click / long-press cell menu
      // core grids have (Show matching · Filter out · Copy, plus
      // page-specific row actions via `extraItems` — the AdrGrid pattern).
      // Since SDK 1.30 it also resolves `useDragFill`, so an extension grid
      // with editable cells offers the Inventory's Excel-style fill-down
      // under the same five invariants (write-then-reload through the page's
      // own per-cell primitive, one wrapper ref borrowed from
      // `useColumnFreeze`, never a bulk endpoint). It rides the same chunk —
      // MUI plus `import type` from ag-grid — so nothing joins the eager
      // bundle.
      loadAgGrid: () =>
        Promise.all([
          import("ag-grid-react"),
          import("@/lib/agGridSetup"),
          import("@/components/grid/useColumnFreeze"),
          import("@/components/grid/useColumnOrder"),
          import("@/components/grid/useCellContextMenu"),
          import("@/components/grid/useDragFill"),
        ]).then(([agReact, setup, freeze, order, cellMenu, dragFill]) => ({
          AgGridReact: agReact.AgGridReact,
          gridThemeLight: setup.gridThemeLight,
          gridThemeDark: setup.gridThemeDark,
          useColumnFreeze: freeze.useColumnFreeze,
          useColumnOrder: order.useColumnOrder,
          useCellContextMenu: cellMenu.useCellContextMenu,
          useDragFill: dragFill.useDragFill,
        })),
      CreateCardDialog: ExtensionCreateCardDialog,
      // SDK 1.21 — timeline + dependency-view + filter-sidebar reuse.
      // `loadTimeline` resolves the time-travel slider and its pure helpers
      // in one call, so an extension timeline shares the exact semantics the
      // core reports have (range computation, arriving/retired classification,
      // milestone clustering, the persisted `timelineDate` convention via
      // `useTimeline`). An async loader, not a static import: the slider only
      // lives in report chunks today and must not join the eager main bundle.
      loadTimeline: () =>
        Promise.all([
          import("@/components/TimelineSlider"),
          import("@/features/reports/timelineRange"),
          import("@/hooks/useTimeline"),
        ]).then(([slider, range, timeline]) => ({
          TimelineSlider: slider.default,
          useTimeline: timeline.useTimeline,
          computeTimelineRange: range.computeTimelineRange,
          classifyTimelineChange: range.classifyTimelineChange,
          isPresentAtDate: range.isPresentAtDate,
          isVisibleAtDate: range.isVisibleAtDate,
          computeTimelineMilestones: range.computeTimelineMilestones,
          cardsChangingBetween: range.cardsChangingBetween,
        })),
      // `loadDependencyView` resolves the house dependency notation (the
      // Layered Dependency View) plus its layout engine from core's own
      // code-split chunk (React Flow + dagre ride along) — the same
      // reuse-over-rebuild posture as `loadRecharts`/`loadAgGrid`, and the
      // §3.10 rule applies: never substitute another graph library. The
      // layout module is handed back as a namespace so its node/edge
      // builders and helpers stay available without re-listing them here.
      loadDependencyView: () =>
        Promise.all([
          import("@/features/reports/LayeredDependencyView"),
          import("@/features/reports/layeredDependencyLayout"),
        ]).then(([view, layout]) => ({
          LayeredDependencyView: view.default,
          layeredDependencyLayout: layout,
        })),
      // The §3.11 filter-sidebar building blocks — section header with icon +
      // count chip, dense checkbox rows with semantic dots/icons and the
      // per-row freeze pin — so an extension grid page's sidebar is built
      // from the same anatomy as the Inventory's instead of a lookalike.
      FilterSectionHeader,
      FilterCheckboxList,
      ColumnFreezeToggle,
      // `loadReportExport` resolves core's own XLSX/PPTX report exporters.
      // An extension that wants a deck must NOT bundle a presentation
      // library: the engine that renders a core report to PowerPoint is the
      // one that should render an extension's, so the decks match and the
      // heavy dependency stays in the one code-split chunk that already
      // carries it. Async for exactly that reason.
      loadReportExport: () =>
        import("@/features/reports/reportExport").then((module) => ({
          exportReportToPptx: module.exportReportToPptx,
          exportReportToXlsx: module.exportReportToXlsx,
          extractSheetsFromDOM: module.extractSheetsFromDOM,
        })),
      // SDK 1.30 — the spreadsheet engine an extension GRID needs, as
      // distinct from the report exporters above: `exportCurrentViewToExcel`
      // / `buildCurrentViewWorkbook` are the Inventory's own WYSIWYG "export
      // what is on screen" (rows and displayed columns read off the grid
      // API), the two readers are the leaf `lib/spreadsheet.ts` lifted out
      // of the card importer, and `xlsx` is the vendored library itself (the
      // `loadRecharts` posture for a bare heavy dependency) so a caller can
      // append its own machine-readable sheet to the workbook core built.
      // An extension must never bundle a spreadsheet library of its own.
      loadSpreadsheet: () =>
        Promise.all([
          import("xlsx"),
          import("@/features/inventory/excelExport"),
          import("@/lib/spreadsheet"),
        ]).then(([xlsx, exp, sheet]) => ({
          xlsx,
          exportCurrentViewToExcel: exp.exportCurrentViewToExcel,
          buildCurrentViewWorkbook: exp.buildCurrentViewWorkbook,
          parseWorkbook: sheet.parseWorkbook,
          readWorkbookSheets: sheet.readWorkbookSheets,
        })),
    },
    register: registerExtension,
  };
}

/**
 * Fetch the entitlement-filtered manifest and import each bundle.
 * Call once after login; per-extension failures are recorded and
 * surfaced, never thrown.
 */
export async function loadUiExtensions(): Promise<void> {
  if (_loadStarted) return;
  _loadStarted = true;
  initExtensionHost();
  let manifest: UiManifestEntry[] = [];
  try {
    manifest = await api.get<UiManifestEntry[]>("/extensions/ui-manifest");
  } catch {
    // Backend older than the extension store, or transient failure — no UI extensions.
    return;
  }
  for (const entry of manifest) {
    if (entry.name) _displayNames = { ..._displayNames, [entry.key]: entry.name };
  }
  await Promise.all(
    manifest.map(async (entry) => {
      try {
        await import(/* @vite-ignore */ entry.entry);
        if (!_registered.some((r) => r.key === entry.key)) {
          _loadErrors = {
            ..._loadErrors,
            [entry.key]: "bundle imported but never called TurboEA.register()",
          };
          notify();
        }
      } catch (e) {
        _loadErrors = {
          ..._loadErrors,
          [entry.key]: e instanceof Error ? e.message : String(e),
        };
        notify();
      }
    }),
  );
}

// ---------------------------------------------------------------------------
// Route outlet — one wildcard SPA route renders all extension pages
// ---------------------------------------------------------------------------

/** All route contributions across registered extensions. */
export function getExtensionRoutes(): { extKey: string; route: ExtensionRouteContribution }[] {
  return _registered.flatMap(({ key, plugin }) =>
    (plugin.routes ?? []).map((route) => ({ extKey: key, route })),
  );
}

/** Extension routes that requested placement into a given core nav group. */
export function getExtensionRoutesForGroup(
  group: ExtensionNavGroup,
): { extKey: string; route: ExtensionRouteContribution }[] {
  return _registered.flatMap(({ key, plugin }) =>
    (plugin.routes ?? [])
      .filter((r) => r.navGroup === group)
      .map((route) => ({ extKey: key, route })),
  );
}

/**
 * The display name the UI manifest reported for an extension, or its key when
 * the manifest carried none (a backend older than 2.128.0, or a bundle that
 * never loaded). Test helper `setExtensionDisplayName` seeds it.
 */
export function getExtensionDisplayName(key: string): string {
  return _displayNames[key] ?? key;
}

/** Test helper — what `loadUiExtensions` records from the manifest. */
export function setExtensionDisplayName(key: string, name: string): void {
  _displayNames = { ..._displayNames, [key]: name };
}

/**
 * Whether the viewer may open an extension page at `path` (an `/ext/<key>/…`
 * link a notification or a todo carries). Fails CLOSED: no registered route
 * matches ⇒ false — the bundle is not loaded or not entitled, so the page
 * would be "not found" — and a route that declares a `permission` needs the
 * viewer to hold it. Query string and hash are ignored; a route matches its
 * own path or a `/`-boundary prefix of it (`/ext/x` covers `/ext/x/runs`).
 */
export function canOpenExtensionPath(
  path: string,
  perms: Record<string, boolean> | undefined,
): boolean {
  const pathname = path.split(/[?#]/, 1)[0] ?? "";
  if (!pathname.startsWith("/ext/")) return false;
  for (const { route } of getExtensionRoutes()) {
    const base = route.path.replace(/\/+$/, "");
    if (pathname !== base && !pathname.startsWith(`${base}/`)) continue;
    return route.permission ? hasPermission(perms, route.permission) : true;
  }
  return false;
}

// ---------------------------------------------------------------------------
// Error boundary — wraps every extension-provided component
// ---------------------------------------------------------------------------

interface BoundaryProps {
  extensionKey: string;
  children: React.ReactNode;
}

interface BoundaryState {
  error: string | null;
}

export class ExtensionBoundary extends React.Component<BoundaryProps, BoundaryState> {
  state: BoundaryState = { error: null };

  static getDerivedStateFromError(error: unknown): BoundaryState {
    return { error: error instanceof Error ? error.message : String(error) };
  }

  componentDidCatch(error: unknown) {
    console.error(`[extension:${this.props.extensionKey}]`, error);
  }

  render() {
    if (this.state.error) {
      return (
        <Alert severity="error" variant="outlined" sx={{ m: 1 }}>
          Extension “{this.props.extensionKey}” failed to render: {this.state.error}
        </Alert>
      );
    }
    return this.props.children;
  }
}
